import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {Subject, debounceTime, takeUntil} from "rxjs";
import {AdminService} from "../services/admin.service";
import {MatDialog} from "@angular/material/dialog";
import {EditCoachStatusComponent} from "./edit-coach-status/edit-coach-status.component";
import {CoachApplication, Course, Pagination} from "../../shared/SharedEntities";
import {AuthenticationService} from "../../auth/services/authentication.service";
import {User} from "../../auth/models/User";

// The backend's coach-applications endpoint only supports filtering by
// `name` and `personalNumber` (no course filter) - see GET /api/v1/coach-applications
// in the API's OpenAPI spec. To still support filtering by course, we fetch every
// application matching the name/personalNumber filters and filter/paginate by
// course on the client.
const FETCH_ALL_SIZE = 5000;

@Component({
  selector: 'app-coach-applications',
  templateUrl: './coach-applications.component.html',
  styleUrls: ['./coach-applications.component.scss']
})
export class CoachApplicationsComponent implements OnInit, AfterViewInit, OnDestroy {
  coachApplications = new MatTableDataSource<any>();
  displayedColumns: string[] = ['name', 'surname', 'courseName', 'personalNumber', 'email', 'dateOfBirth', 'gender', 'status'];
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  pageSize = 10;
  currentUser!: User;
  courses: Course[] = [];
  filterCourseId: string = '';
  filterPersonalNumber: string = '';
  filterName: string = '';
  private allResults: CoachApplication[] = [];
  private filterChanged$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(
    private adminService: AdminService,
    public dialog: MatDialog,
    private authenticationService: AuthenticationService,
  ) {
    this.authenticationService.currentUser.pipe(takeUntil(this.destroy$)).subscribe(x => {
      this.currentUser = x!;
    });

    this.filterChanged$.pipe(
      debounceTime(400),
      takeUntil(this.destroy$)
    ).subscribe(() => this.loadData());
  }

  ngOnInit() {
    this.adminService.getAllCourses().subscribe((result: Course[]) => {
      this.courses = result;
    }, (error: any) => {
      console.error(error);
    });
  }

  ngAfterViewInit() {
    this.coachApplications.paginator = this.paginator;
    this.loadData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData() {
    if (this.currentUser) {
      this.adminService.getAllCoachApplications(0, FETCH_ALL_SIZE, this.filterPersonalNumber, this.filterName).subscribe(
        (result: Pagination<CoachApplication>) => {
          this.allResults = result.content;
          this.applyCourseFilter();
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }

  openStatusDialog(coachApplication: any): void {
    const dialogRef = this.dialog.open(EditCoachStatusComponent, {
      width: '800px',
      maxHeight: '90vh',
      data: {coachApplication}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadData();
      }
    });
  }

  onCourseFilterChange() {
    this.applyCourseFilter();
  }

  onFilterInputChange() {
    this.filterChanged$.next();
  }

  clearFilter() {
    this.filterCourseId = '';
    this.filterPersonalNumber = '';
    this.filterName = '';
    this.loadData();
  }

  private applyCourseFilter(): void {
    this.coachApplications.data = this.filterCourseId
      ? this.allResults.filter(coach => coach.courseId === this.filterCourseId)
      : this.allResults;

    if (this.paginator) {
      this.paginator.firstPage();
    }
  }
}
